import {
  ReactElement,
  useState,
  useMemo,
  useEffect,
} from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  HiOutlinePlayCircle,
} from 'react-icons/hi2';
import Button from '../../components/Button';
import { numberToLetters } from '../../utils/letterNumber';
import {
  DISC_LETTER_NUMBER,
} from '../../constants/apiConstants';
import {
  useSetWheelAndReprocess,
} from '../../hooks/useSessions';

const PARAM_FIELDS = [
  'wheel_circumference',
  'cda_ref',
  'cp_corr',
  'yaw_offset',
] as const;

const PARAM_HEADERS = [
  'Wheel Circ.',
  'CdA Ref',
  'Cp Corr',
  'Yaw Offset',
] as const;

interface ParametersCoordinationProps {
  sessions: any[];
  selectedSessionId: string;
}

function getValidConfigs(session: any): any[] {
  if (!session?.configs) return [];
  return session.configs
    .filter(
      (c: any) =>
        !c.isDeleted &&
        c.letter_number !== undefined &&
        c.letter_number < DISC_LETTER_NUMBER,
    )
    .sort(
      (a: any, b: any) =>
        (a.letter_number ?? 0) - (b.letter_number ?? 0),
    );
}

function formatVal(v: any): string {
  if (v == null) return '-';
  const n = parseFloat(String(v));
  if (isNaN(n)) return '-';
  return n.toPrecision(5);
}

function sessionLabel(session: any): string {
  const date =
    session.date ||
    (session.created_at
      ? new Date(session.created_at).toLocaleDateString()
      : '');
  const desc =
    session.description || session.name || 'Unnamed';
  return `${date} – ${desc}`;
}

function ViewParamPair({
  config,
  refConfig,
  refConfigs,
  refId,
  stripe,
  onRefChange,
}: {
  config: any;
  refConfig: any;
  refConfigs: any[];
  refId: string;
  stripe: string;
  onRefChange: (refId: string) => void;
}): ReactElement {
  const letter = numberToLetters(
    config.letter_number,
  );
  return (
    <>
      {/* Row 1: Viewed session values */}
      <tr
        className={
          'cr-border-b cr-border-gray-100 ' + stripe
        }
      >
        <td
          className={
            'cr-px-3 cr-py-1.5 cr-font-semibold'
          }
        >
          {letter}
        </td>
        <td className="cr-px-3 cr-py-1.5">
          {config.description || '-'}
        </td>
        {PARAM_FIELDS.map((f) => (
          <td key={f} className="cr-px-3 cr-py-1.5">
            {formatVal(config[f])}
          </td>
        ))}
      </tr>
      {/* Row 2: Reference session values */}
      <tr
        className={
          'cr-border-b-2 cr-border-gray-300 '
          + stripe
        }
      >
        <td className="cr-px-3 cr-py-1.5">
          <select
            value={refId ?? ''}
            onChange={(e) =>
              onRefChange(e.target.value)
            }
            className={
              'cr-w-16 cr-p-1 cr-text-sm '
              + 'cr-border cr-rounded '
              + 'cr-border-blue-400 cr-bg-blue-50'
            }
          >
            {refConfigs.map((rc2: any) => (
              <option key={rc2.id} value={rc2.id}>
                {numberToLetters(rc2.letter_number)}
              </option>
            ))}
          </select>
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5 cr-text-xs '
            + 'cr-italic cr-text-blue-500'
          }
        >
          {refConfig?.description || '-'}
        </td>
        {PARAM_FIELDS.map((f) => (
          <td
            key={f}
            className={
              'cr-px-3 cr-py-1.5 cr-text-blue-500'
            }
          >
            {formatVal(refConfig?.[f])}
          </td>
        ))}
      </tr>
    </>
  );
}

export default function ParametersCoordination({
  sessions,
  selectedSessionId,
}: ParametersCoordinationProps): ReactElement {
  const queryClient = useQueryClient();
  const wheelAndReprocess = useSetWheelAndReprocess(
    selectedSessionId,
  );

  const [viewSessionId, setViewSessionId] = useState(
    sessions[1]?.id ?? sessions[0]?.id ?? '',
  );

  const refSession = sessions[0];
  const refConfigs = useMemo(
    () => getValidConfigs(refSession),
    [refSession],
  );

  const viewSession = useMemo(
    () => sessions.find((s) => s.id === viewSessionId),
    [sessions, viewSessionId],
  );

  const viewConfigs = useMemo(
    () => getValidConfigs(viewSession),
    [viewSession],
  );

  const [refMapping, setRefMapping] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const m: Record<string, string> = {};
    viewConfigs.forEach((vc: any, i: number) => {
      const rc = refConfigs[i] ?? refConfigs[0];
      if (rc) m[vc.id] = rc.id;
    });
    setRefMapping(m);
  }, [viewSessionId]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [
        'session-tyre-reference',
        selectedSessionId,
      ],
    });
  };

  const handleSetCircumference = async () => {
    try {
      await wheelAndReprocess.mutateAsync();
      toast.success('Wheel circumference set');
      invalidate();
    } catch (err) {
      console.error(
        'Set circumference error:', err,
      );
      toast.error(
        'Failed to set wheel circumference',
      );
    }
  };

  const selectCls =
    'cr-w-full cr-p-2 cr-text-sm cr-text-blue-900 '
    + 'cr-border cr-border-gray-300 cr-rounded-md '
    + 'focus:cr-ring-2 focus:cr-ring-blue-500 '
    + 'focus:cr-border-blue-500';

  const btnCls =
    'cr-px-3 cr-py-1.5 cr-text-sm '
    + '!cr-normal-case !cr-font-bold '
    + '!cr-bg-gray-100 !cr-text-blue-900 '
    + '!cr-border-gray-300';

  return (
    <div className="cr-space-y-4">
      {/* Session to View */}
      <div className="md:cr-w-1/2">
        <label
          className={
            'cr-block cr-mb-1 cr-text-sm '
            + 'cr-font-medium cr-text-blue-900'
          }
        >
          Session to View
        </label>
        <select
          value={viewSessionId}
          onChange={(e) =>
            setViewSessionId(e.target.value)
          }
          className={selectCls}
        >
          {sessions.map((s: any) => (
            <option key={s.id} value={s.id}>
              {sessionLabel(s)}
            </option>
          ))}
        </select>
      </div>

      {/* Parameters Table */}
      {viewConfigs.length > 0 && (
        <div className="cr-overflow-x-auto">
          <table
            className={
              'cr-w-full cr-text-sm cr-text-left '
              + 'cr-text-blue-900'
            }
          >
            <thead>
              <tr
                className={
                  'cr-bg-cirrusBlue-50 cr-text-xs '
                  + 'cr-uppercase'
                }
              >
                <th className="cr-px-3 cr-py-2">
                  Cfg
                </th>
                <th className="cr-px-3 cr-py-2">
                  Description
                </th>
                {PARAM_HEADERS.map((h) => (
                  <th
                    key={h}
                    className="cr-px-3 cr-py-2"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viewConfigs.map(
                (config: any, idx: number) => {
                  const refId =
                    refMapping[config.id];
                  const rc = refConfigs.find(
                    (c: any) => c.id === refId,
                  );
                  const stripe = idx % 2
                    ? 'cr-bg-gray-50'
                    : 'cr-bg-white';
                  return (
                    <ViewParamPair
                      key={config.id}
                      config={config}
                      refConfig={rc}
                      refConfigs={refConfigs}
                      refId={refId}
                      stripe={stripe}
                      onRefChange={(rid) =>
                        setRefMapping((p) => ({
                          ...p,
                          [config.id]: rid,
                        }))
                      }
                    />
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Action Buttons */}
      <div
        className={
          'cr-flex cr-flex-wrap cr-gap-2 cr-pt-2'
        }
      >
        <Button
          onClick={handleSetCircumference}
          disabled={wheelAndReprocess.isPending}
          icon={
            <HiOutlinePlayCircle
              className="cr-size-5"
            />
          }
          className={btnCls}
        >
          {wheelAndReprocess.isPending
            ? 'Processing...'
            : 'Set Circumference'}
        </Button>
      </div>
    </div>
  );
}
